import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessFlowchartConfigEditorQuery} from './__generated/ProcessFlowchartConfigEditorQuery';
import {ProcessFlowchartConfigEditorQueryVariables} from './__generated/ProcessFlowchartConfigEditorQuery';
import {
  ProcessFlowchartConfigVersionEditorQuery,
  ProcessFlowchartConfigVersionEditorQueryVariables,
} from './__generated/ProcessFlowchartConfigVersionEditorQuery';
import ProcessFlowchartConfigEditorController from './ProcessFlowchartConfigEditorController';

export type ProcessFlowchartConfigGQL = ExtractByTypename<
  ProcessFlowchartConfigEditorQuery['processFlowchartConfig'],
  'ProcessFlowchartConfigNode'
>;

export type ProcessFlowchartConfigWithValidSchemaGQL = ProcessFlowchartConfigGQL & {
  schema: ExtractByTypename<ProcessFlowchartConfigGQL['schema'], 'ProcessTaskSchemaNode'>;
};
export type ProcessFlowchartConfigVersionGQL = ExtractByTypename<
  ProcessFlowchartConfigVersionEditorQuery['processFlowchartConfigVersion'],
  'ProcessFlowchartConfigVersionNode'
>;

export const PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigEditorQuery($flowchartConfigId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROCESS_FLOWCHART_CONFIG_VERSION_EDITOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigVersionEditorQuery($flowchartConfigVersionId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowchartConfigVersion(id: $flowchartConfigVersionId) {
      ... on ProcessFlowchartConfigVersionNode {
        ...ProcessFlowchartConfigVersionForEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigVersionForEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function NonConfigurableSchema(): JSX.Element {
  return <div>This flowchart is not user-configurable.</div>;
}

function NoFlowchartConfigVersion(): JSX.Element {
  return <div>No flowchart config version found</div>;
}

interface ProcessFlowchartConfigEditorProps {
  flowchartConfigId: string;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator<unknown>;
  flowchartConfigVersionId?: string;
  locked: boolean;
}

/**
 * Process flowchart config editor that queries the flowchart config and displays the
 * flowchart editor content.
 *
 * Renders the current flowchart config version unless a specific version is specified.
 */
export default function ProcessFlowchartConfigEditor(props: ProcessFlowchartConfigEditorProps): JSX.Element {
  return (
    <BenchlingQuery<ProcessFlowchartConfigEditorQuery, ProcessFlowchartConfigEditorQueryVariables>
      query={PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{flowchartConfigId: props.flowchartConfigId}}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) =>
        (() => {
          if (isInaccessible(data.processFlowchartConfig)) {
            return (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={data.processFlowchartConfig}
                modelType="processFlowchartConfig"
              />
            );
          }
          const flowchartConfigNode = data.processFlowchartConfig;

          if (
            isInaccessible(flowchartConfigNode.schema) ||
            !canConfigureProcessTaskSchema(flowchartConfigNode.schema)
          ) {
            return <NonConfigurableSchema />;
          }

          const currentFlowchartConfigVersion = flowchartConfigNode.currentFlowchartConfigVersion;
          if (currentFlowchartConfigVersion == null) {
            return <NoFlowchartConfigVersion />;
          }

          if (
            props.flowchartConfigVersionId == null ||
            props.flowchartConfigVersionId === currentFlowchartConfigVersion.id
          ) {
            return (
              <ProcessFlowchartConfigEditorController
                flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                // flowchartConfigNode.schema guaranteed by checks above
                flowchartConfig={flowchartConfigNode as ProcessFlowchartConfigWithValidSchemaGQL}
                flowchartConfigVersion={currentFlowchartConfigVersion}
                locked={props.locked}
              />
            );
          }

          // Query the specified flowchart config version
          return (
            <BenchlingQuery<
              ProcessFlowchartConfigVersionEditorQuery,
              ProcessFlowchartConfigVersionEditorQueryVariables
            >
              query={PROCESS_FLOWCHART_CONFIG_VERSION_EDITOR_QUERY}
              renderLoading={() => <CenteredHorizontalThrobber />}
              variables={{flowchartConfigVersionId: props.flowchartConfigVersionId}}
              fetchPolicy="cache-and-network"
            >
              {(innerData, {refetch: innerRefetch}) => {
                if (isInaccessible(innerData.processFlowchartConfigVersion)) {
                  return (
                    <WorkspaceItemInaccessibleGuard
                      inaccessibleInfo={innerData.processFlowchartConfigVersion}
                      modelType="processFlowchartConfigVersion"
                    />
                  );
                }

                if (innerData.processFlowchartConfigVersion == null) {
                  return <NoFlowchartConfigVersion />;
                }

                return (
                  <ProcessFlowchartConfigEditorController
                    flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                    /// flowchartConfigNode.schema guaranteed by checks above
                    flowchartConfig={flowchartConfigNode as ProcessFlowchartConfigWithValidSchemaGQL}
                    flowchartConfigVersion={innerData.processFlowchartConfigVersion}
                    locked={props.locked}
                  />
                );
              }}
            </BenchlingQuery>
          );
        })()
      }
    </BenchlingQuery>
  );
}
